import type { ShowOrDeleteQuotes } from '@/types/policy/PolicyTypes'
import { getToken } from '@/utils/KeycloakUtils'
import type { Policy } from 'policies-api'
import type { MotorContract } from '@/types/motor/MotorContract'
import { type BlockedContracts } from 'policies-api/dist/models/BlockedContracts'
import { partnerApi } from '@/stores/partnerApiClient'

export function sortPoliciesByStatus(a: Policy, b: Policy): number {
  const nameA = a.policyStatus!.toUpperCase() // ignore upper and lowercase
  const nameB = b.policyStatus!.toUpperCase() // ignore upper and lowercase
  if (nameA < nameB) {
    return -1
  }
  if (nameA > nameB) {
    return 1
  }

  // names must be equal
  return 0
}

export function sortPoliciesByDateDesc(a: Policy, b: Policy): number {
  const nameA = a.beginningDatePolicy!
  const nameB = b.beginningDatePolicy!
  if (nameA < nameB) {
    return 1
  }
  if (nameA > nameB) {
    return -1
  }

  // names must be equal
  return 0
}

export async function fetchAllPoliciesByPartnerProxyId(
  partnerProxyId: string
): Promise<Policy[]> {
  const token = await getToken()

  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/contracts?` +
      new URLSearchParams({
        partnerProxyId: partnerProxyId
      }),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      },
      method: 'GET'
    }
  ).then(async (response) => {
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    return await response.json()
  })
}

export async function fetchPolicyByPolicyId(policyId: string): Promise<Policy> {
  const token = await getToken()

  return fetch(`${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/contracts/${policyId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      KeyId: globalThis.ENV_CONFIG?.API_KEY,
      'Content-Type': 'application/json'
    },
    method: 'GET'
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    return await response.json()
  })
}

export async function fetchLockedContractsCount(partnerProxyId: string): Promise<number> {
  const token = await getToken()

  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/contracts/locked?` +
      new URLSearchParams({
        partnerProxyId: partnerProxyId
      }),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      },
      method: 'GET'
    }
  ).then(async (response) => {
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const jsonResp: BlockedContracts = await response.json()
    return jsonResp.count ?? 0
  })
}

export async function fetchAllQuoteIdsByPartnerProxyId(
  partnerProxyId: string
): Promise<string[]> {
  const response = await partnerApi().getQuotes({ partnerProxyId })
  return response.data ?? []
}

export async function fetchQuoteShowOrDelete(
  quoteId: string
): Promise<ShowOrDeleteQuotes> {
  const token = await getToken()

  let motorContract: MotorContract | null = null

  const code = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/quotes?` +
      new URLSearchParams({
        quoteId: quoteId
      }),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      },
      method: 'GET'
    }
  ).then(async (response) => {
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    // Only HTTP 200 contains a MotorContract. Responses with HTTP 204 or 208
    // are intentionally empty and must not be parsed as JSON.
    if (response.status === 200) motorContract = await response.json()
    return response.status
  })

  switch (code) {
    case 200:
      return {
        showCard: true,
        deleteFromQuotes: false,
        showDetails: true,
        motorContract: motorContract
      }
    case 204:
      return {
        showCard: true,
        deleteFromQuotes: false,
        showDetails: false,
        motorContract: motorContract
      }
    case 208:
      return {
        showCard: false,
        deleteFromQuotes: true,
        showDetails: false,
        motorContract: motorContract
      }
    default:
      return {
        showCard: false,
        deleteFromQuotes: false,
        showDetails: false,
        motorContract: motorContract
      }
  }
}

export async function deleteQuoteIdFromList(quoteId: string): Promise<void> {
  await partnerApi().deleteQuote({ quoteId })
}

export async function requestTaxConfirmation(requestString: string) {
  const token = await getToken()
  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/self-services/tax-confirmation${requestString}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      },
      method: 'GET'
    }
  )
}

export async function requestIvkCertificate(policyId: string): Promise<Response> {
  const token = await getToken()
  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/self-services/certificates/ivk?policyId=${policyId}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      }
    }
  )
}
